import {
  useAsyncCall,
  AsyncCallArgs,
  GridContainer,
  GridItem,
  SubmitFormButton,
  handleFormSubmission,
  useReactHookForm,
  SelectFormField,
  AsyncAutocompleteWithAdd,
  InputFormField,
  FormSection,
  CheckboxFormField,
  IsActiveFilter,
} from "@duosoftbg/nacid-components";
import { Typography } from "@mui/material";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useAppDispatch from "../../../../../../../hooks/redux/base/useAppDispatch";
import { toast } from "react-toastify";
import {
  createHomePageCategory,
  getRolesWithAdditionalParamsAutocomplete,
  updateHomePageCategory,
} from "../../../../../../../axios/api/services";
import React, { useEffect, useState } from "react";
import { CategoryDetails } from "../../../../../../../types/homePageCategoryTypes";
import { createCategoryValidationSchema } from "../../../../../../../yup/schema/homePageCategory/homePageCategoryValidationSchema";
import { addCategoryViewObject } from "../../HomePageCategoryManagement";
import { homePageCategoryIcons } from "../../../../../../../utils/constants";
import ScrollableGridContainer from "../../../../../../components/common/grid/ScrollableGridContainer";
import AccessRolesDialogList from "../AccessRolesDialogList";

const EditCategoryDialogBody = ({ setOpen, isEditMode, category }) => {
  const { t } = useTranslation();
  const { asyncCall } = useAsyncCall();
  const dispatch = useAppDispatch();
  const [roles, setRoles] = useState([]);

  const logoOptions = [
    { value: homePageCategoryIcons.reportsLogo, text: t("l.reports.logo") },
    { value: homePageCategoryIcons.libraryServicesLogo, text: t("l.libserv.logo") },
    { value: homePageCategoryIcons.apostilleLogo, text: t("l.apostille.logo") },
    { value: homePageCategoryIcons.certificatesLogo, text: t("l.certificates.logo") },
    { value: homePageCategoryIcons.academicRecognitionLogo, text: t("l.academic.recognition.logo") },
    { value: homePageCategoryIcons.registersLogo, text: t("l.registers.logo") },
    { value: homePageCategoryIcons.commonServicesLogo, text: t("l.common.services.logo") },
    { value: homePageCategoryIcons.impactFactorLogo, text: t("l.impact.factor.logo") },
    { value: homePageCategoryIcons.profQualificationLogo, text: t("l.prof.qualification.logo") },
  ];

  const { methods } = useReactHookForm<CategoryDetails>({
    defaultValues: category,
    validationSchema: createCategoryValidationSchema,
  });

  useEffect(() => {
    setRoles(category.accessRoles);
  }, [category]);

  const submit = (values) => {
    const asyncSaveGroup: AsyncCallArgs = {
      withGlobalBackdrop: true,
      promise: values.id !== "" ? updateHomePageCategory(values) : createHomePageCategory(values),
      reactHooksForm: { methods },
      onSuccess: (response) => {
        setOpen(false);
        addCategoryViewObject(response, dispatch);
        toast.success(t("m.save.data.success"));
      },
    };
    asyncCall(asyncSaveGroup);
  };

  const handleAddRoleToUser = (role) => {
    const accessRoles = methods.getValues("accessRoles");
    if (!accessRoles.find((arrayRole) => arrayRole === role.name)) {
      methods.setValue("accessRoles", [...accessRoles, role.name]);
      setRoles([...accessRoles, role.name]);
    }
  };

  const handleRemoveRoleClick = (role) => {
    const newRoles = roles.filter((existingRole) => existingRole !== role);
    methods.setValue("accessRoles", [...newRoles]);
    setRoles([...newRoles]);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleFormSubmission({ methods, onSubmitFn: submit, t })}>
        <FormSection label={"t.main.details"}>
          <GridContainer spacing={3} mt={1}>
            <GridItem sm={4} md={4}>
              <SelectFormField
                fieldName={"icon"}
                labelCode={"l.logo"}
                addEmptyOption={true}
                selectOptions={logoOptions}
              />
            </GridItem>

            <IsActiveFilter sm={4} md={4} fieldName={"active"} />

            <GridItem sm={4} md={4}>
              <InputFormField fieldName={"order"} labelCode={"l.input.home.category.position"} />
            </GridItem>
            <GridItem sm={12} md={12}>
              <InputFormField fieldName={"name"} labelCode={"l.description"} />
            </GridItem>

            <GridItem sm={12} md={12}>
              <InputFormField fieldName={"nameEn"} labelCode={"l.description.en"} />
            </GridItem>
            <GridItem sm={12} md={12}>
              <CheckboxFormField fieldName={"showInServicesPage"} labelCode={"l.showInServicesPage"} />
            </GridItem>
          </GridContainer>
        </FormSection>
        <FormSection label={"t.user.roles"}>
          <GridContainer spacing={3} mt={1}>
            <AsyncAutocompleteWithAdd
              key={`roles-autocomplete-key-${methods.getValues("accessRoles").length}`}
              onAddRecord={handleAddRoleToUser}
              setOptionText={(option) => option.name}
              getOptionLabel={(option) => option.id}
              setInputOnSelect={(option) => option.name}
              autocompleteFn={getRolesWithAdditionalParamsAutocomplete}
              label={"l.select.role"}
              autocompletePropsGrid={{ sm: 12, md: 11.5 }}
            />
            <ScrollableGridContainer>
              <AccessRolesDialogList roles={roles} isEdit={true} onRemove={handleRemoveRoleClick} />
            </ScrollableGridContainer>
          </GridContainer>
        </FormSection>
        <GridContainer spacing={2}>
          <GridItem sm={12} md={12}>
            <Typography align={"right"}>
              <SubmitFormButton label={"l.btn.save"} />
            </Typography>
          </GridItem>
        </GridContainer>
      </form>
    </FormProvider>
  );
};

export default EditCategoryDialogBody;
